"use client";

import { Comedian, SpecialRow, useSession } from "@punchup/data/legacy";
import { SpecialPageEvents, trackEvent } from "@punchup/analytics";

import Link from "next/link";
import { VideoTitleHeader } from "../common";
import { formatRuntime } from "@punchup/utils/formatRuntime";

interface Props {
  comedian: Comedian;
  special: SpecialRow;
}

export default function SpecialPreviewPrompt({ comedian, special }: Props) {
  const session = useSession();

  const onComedianLinkClick = () => {
    trackEvent(SpecialPageEvents.CLICKED_COMEDIAN_NAME_LINK_CHECKOUT, {
      comedianID: comedian.id,
      specialID: special.id,
    });
  };

  const displayName = comedian.display_name_short
    ? comedian.display_name_short
    : comedian.display_name;

  const videoOrLivestreamText = special.livestream
    ? "live stream"
    : `full ${formatRuntime(special.runtime)} video`;

  return (
    <div className="px-4 md:px-0">
      <div className="flex flex-col gap-2 mt-2">
        <VideoTitleHeader className="text-2xl break-words">
          {special.title}
          <span className="whitespace-normal">
            {" "}
            |{" "}
            <Link
              href={`/${comedian.slug}`}
              className="hover:underline underline-offset-2"
              onClick={onComedianLinkClick}
            >
              {comedian.display_name}
            </Link>
          </span>
        </VideoTitleHeader>
      </div>
      {/*
       * NOTE: ensure "Join {comedian.display_name_short}'s guest list" is present in all cases
       * We check for the presence of this text in our end-to-end tests
       * */}
      {session?.user && special.requires_subscription ? (
        <p className="text-secondary-blue">
          Almost there! Join {displayName}&apos;s guest list to get access to
          the {videoOrLivestreamText} and get exclusive updates – no spam, just
          the good stuff!
        </p>
      ) : session?.user && special.requires_purchase ? (
        <p className="mt-1 text-sm text-secondary-blue">
          Almost there! You are about to purchase {`${displayName}'s`}{" "}
          {videoOrLivestreamText} for <b>${special.price}</b> USD. Join{" "}
          {displayName}&apos;s guest list to get exclusive updates – no spam,
          just the good stuff!
        </p>
      ) : (
        <p className="mt-1 text-sm text-secondary-blue">
          Join {displayName}&apos;s guest list to watch the{" "}
          {videoOrLivestreamText} and get exclusive updates!
        </p>
      )}
    </div>
  );
}
